import { useEffect, useState, type FormEvent } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'

import { AuthError, useAuth } from '../AuthContext'

const MIN_PASSWORD_LEN = 12

/**
 * The one input treatment, so the two fields cannot drift apart. Matches the
 * admin's other inputs, and adds the focus ring the rest of the portal has —
 * this page is where a keyboard user starts, so it is the worst place to be
 * missing one.
 */
const INPUT_CLASS =
  'h-11 w-full rounded-[10px] border border-line-2 bg-bg-app px-3 text-sm text-ink-1 placeholder:text-ink-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

const ROLE_NOT_PERMITTED = 'This account cannot use the admin.'

/** Map the server's (or network) error code to a user-facing message.
 * Mirrors the deliberately-vague server copy for credential failures. */
function messageForError(err: unknown): string {
  const code = err instanceof AuthError ? err.code : ''
  switch (code) {
    case 'invalid_credentials':
      return 'Email or password is incorrect.'
    case 'rate_limited':
      return 'Too many attempts. Try again in a few minutes.'
    case 'network_error':
      return 'Could not reach the server. Try again.'
    case 'role_not_permitted':
      return ROLE_NOT_PERMITTED
    default:
      return 'Something went wrong. Please try again.'
  }
}

/**
 * The admin's front door (M23-T15).
 *
 * **No frame draws it** (M23 gap B5), so it is extended from T03's atoms rather
 * than ported — M16-T09's precedent. It matters more than the missing frame
 * suggests: this is the first thing an admin sees, and left on its old styling
 * the portal would introduce itself in a visual language it abandons one click
 * later.
 *
 * Behaviour, error copy and error handling are untouched. This is paint.
 */
export function LoginPage() {
  const { user, login } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = params.get('next') || '/admin'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  // Signed out by `ProtectedRoute` because the role is not admitted (M36-T03).
  const location = useLocation()
  const signedOut = (location.state as { signedOut?: string } | null)?.signedOut
  const [error, setError] = useState<string | null>(
    signedOut === 'role_not_permitted' ? ROLE_NOT_PERMITTED : null,
  )
  const [submitting, setSubmitting] = useState(false)

  // Already signed in (e.g. navigated here directly) → go where they meant to.
  useEffect(() => {
    if (user) navigate(next, { replace: true })
  }, [user, next, navigate])

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(email, password)
      setPassword('') // never keep the password around past the submit
      navigate(next, { replace: true })
    } catch (err) {
      setPassword('')
      setError(messageForError(err))
      setSubmitting(false)
    }
  }

  return (
    <div className="grid min-h-screen place-items-center bg-bg-soft px-4 py-8 font-sans text-ink-1">
      <div className="w-full max-w-sm rounded-2xl border border-line-1 bg-bg-app p-8">
        {/* The same wordmark, in the same display serif, as the sidebar the
            admin lands in. */}
        <p className="font-display text-lg font-semibold text-ink-1">Medcor</p>
        <h1 className="mt-6 font-display text-2xl font-semibold text-ink-1">
          Sign in
        </h1>
        <p className="mt-1 text-sm text-ink-3">
          The hospital and AMTC admin portal.
        </p>

        <form
          className="mt-6 flex flex-col gap-4"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="email" className="text-sm font-medium text-ink-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="password"
              className="text-sm font-medium text-ink-2"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              minLength={MIN_PASSWORD_LEN}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          {error && (
            <p role="alert" className="text-sm font-medium text-bed-bad">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-2 inline-flex h-12 items-center justify-center rounded-[8px] bg-accent px-4 text-sm font-medium text-accent-on hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="mt-6 text-xs text-ink-3">
          Forgot your password? Contact AMTC to have it reset.
        </p>
      </div>
    </div>
  )
}
